import { FormControlLabel, Grid, makeStyles, Typography } from '@material-ui/core';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { clienteActions, selectStatus } from '../../features/cliente/clienteSlice';
import { isPerformingActionStatus } from '../../utils/store/store-utils';
import { PERFORMING_ACTION_STATUS } from '../../features/status';
import { clienteAPI } from '../../features/cliente/clienteAPI';
import { setErrorFeedback, setSuccessFeedback } from '../../features/feedback/feedbackSlice';
import { CLOSED_STATUS, DONE_STATUS, FAIL_STATUS, isOpenStatus } from '../../utils/hooks/form/dialog/formDialogUtils';
import QCXFinalFormDialog from '../../shared-components/dialog/QCXFinalFormDialog';
import QCXMoveFocusInside from '../focus-lock/QCXMoveFocusInside';
import QCXSelectTipoDocumentoAutocomplete from '../../shared-components/select-tipo-documento/QCXSelectTipoDocumentoAutocomplete';
import QCXFinalTextField from '../../shared-components/final-text-field/QCXFinalTextField';
import { required } from '../../utils/validators/field/validator';
import { MediaType } from '../../utils/api/api-utils';
import QCXFinalCustomInputFile from '../../shared-components/input-file/QCXFinalCustomInputFile';
import QCXFinalDatePickerField from '../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import QCXFinalCheckboxField from '../../shared-components/final-checkbox-field/QCXFinalCheckboxField';
import QCXFinalSelectField from '../../shared-components/final-select-field/QCXFinalSelectField';
import QCXCircularProgress from '../../shared-components/progress/QCXCircularProgress';
import QCXSelectTipoAutocomplete from '../../shared-components/select-tipo-documento/QCXSelectTipoAutocomplete';

const clientSelectors = {
    selectStatus,
}

const useStyles = makeStyles((theme) => ({
    gridFields: {
        paddingBottom: '16px',
    },
    customGridFields: {
        [theme.breakpoints.up('sm')]: {
            paddingLeft: '16px',
        },
        paddingBottom: '16px',
    },
}));

export default function QCXNovoDocumentoClientFormDialog({ parentModel, status, handleStatus, onSuccess, initialModel }) {
    const { t } = useTranslation();
    const classes = useStyles();

    const dispatch = useDispatch();

    const isEditMode = useMemo(() => !!initialModel?.id, [initialModel]);

    const initialValues = useMemo(() => {
        if (!initialModel) return {};

        return {
            id: initialModel.id,
            tipoDocumento: initialModel.tipoDocumento,
            tipo: initialModel.tipo,
            nome: initialModel.nome,
            numero: initialModel.numero,
            dataVencimento: initialModel.vencimento || '',
            notificar: initialModel.notificar,
            documento: initialModel.arquivo ? [initialModel.arquivo] : [],
        }
    }, [initialModel]);

    const handleUpdate = useCallback(
        async (documentId, formData) => {
            const response = await clienteAPI.updateDocumentTo(documentId, formData);

            if (response.status === 200 || response.status === 204) {
                dispatch(
                    setSuccessFeedback({
                        message: t('com.muralis.qcx.mensagem.documentoAtualizadoComSucesso'),
                    })
                );
                return true;
            }
            return false;
        },
        [dispatch, t]
    );

    const statusClient = useSelector(clientSelectors.selectStatus);
    const performingAction = useMemo(() => isPerformingActionStatus(statusClient), [statusClient]);

    const parseToFormData = useCallback((data) => {
        const { documento, notificar, dataVencimento, tipoDocumento, nome, tipo, numero } = data;

        const tipoEnum = tipo;

        const isExistingStorage = documento?.length > 0 && !(documento[0] instanceof File);

        const documentoClienteData = {
            nome,
            tipo: tipoEnum,
            numero,
            notificar: !!notificar,
            ...(dataVencimento ? { dataVencimento } : {}),
            ...(tipoDocumento.id ? {
                tipoDocumento: { id: tipoDocumento.id },
            } : {}),
        };

        const json = JSON.stringify(documentoClienteData);
        const blob = new Blob([json], {
            type: 'application/json',
        });

        const isNewFile = documento?.length > 0 && documento[0] instanceof File;
        const file = isNewFile ? documento[0] : undefined;
        const formData = new FormData();

        formData.append('data', blob);

        if (file) {
            formData.append('file', file);
        }

        return formData;
    }, []);

    const handleSubmit = useCallback(
        async (values) => {
            dispatch(clienteActions.changeStatusTo({ status: PERFORMING_ACTION_STATUS }));

            const client = { id: parentModel?.id };
            let success = false;

            try {
                const formData = parseToFormData(values);

                if (isEditMode && values.id) {
                    success = await handleUpdate(values.id, formData);
                } else {
                    const response = await clienteAPI.addDocumentTo(client, formData);
                    if (response.status === 201) {
                        dispatch(
                            setSuccessFeedback({message: t('com.muralis.qcx.mensagem.documentoAdicionadoComSucesso')})
                        );
                        success = true;
                    }
                }

                if (success) {
                    dispatch(clienteActions.success());
                    handleStatus(DONE_STATUS);

                    if (typeof onSuccess === 'function') {
                        onSuccess();
                    }
                } else {
                    throw new Error("Falha na operação da API.");
                }

            } catch (error) {
                handleStatus(FAIL_STATUS);
                dispatch(clienteActions.failure());
                const errorMessageKey = isEditMode
                    ? t('com.muralis.qcx.erro.erroAtualizarDocumento')
                    : t('com.muralis.qcx.erro.erroAdicionarDocumento');
                dispatch(setErrorFeedback({ message: t(errorMessageKey) }));
            }
        },
        [parentModel, handleStatus, clienteActions, onSuccess, parseToFormData, isEditMode, handleUpdate, t]
    );

    const handleCloseFormDialog = useCallback(() => handleStatus(CLOSED_STATUS), [handleStatus]);

    const open = useMemo(() => isOpenStatus(status), [status]);

    const formDialogTitle = useMemo(
        () => (
            <>
                {isEditMode ? 'EDITAR DOCUMENTO DO CLIENTE' : 'ADICIONAR DOCUMENTO NO CADASTRO DO CLIENTE:'}
                <br />
                <Typography variant="subtitle1">
                    <strong>{parentModel?.nome || parentModel?.numero}</strong>
                </Typography>
            </>
        ),
        [parentModel, isEditMode]
    );

    const formDialogContent = useMemo(
        () => (
            isEditMode
                ? t('com.muralis.qcx.preenchaCamposAnexoArquivoEditarDocumento')
                : t('com.muralis.qcx.preenchaCamposAnexoArquivoNovoDocumento')
        ),
        [isEditMode, t]
    );

    const formDialogActionsProps = useMemo(
        () => ({
            button: {
                submit: {
                    description: isEditMode ? t('com.muralis.qcx.acoes.salvar') : t('com.muralis.qcx.acoes.adicionar'),
                    size: 'small',
                    disabled: performingAction,
                    children: performingAction && <QCXCircularProgress color="disabled" size={20} />,
                },
                cancel: {
                    description: t('com.muralis.qcx.acoes.cancelar'),
                    size: 'small',
                    disabled: performingAction,
                },
            },
        }),
        [performingAction, isEditMode]
    );

    const tipoDocumentoFilterParams = useMemo(() => ({
        cadastroCliente: true,
    }), []);

    return (
        <QCXFinalFormDialog
            key="form-dialog-adicionar-documento-client"
            id="form-dialog-adicionar-documento-client"
            title={formDialogTitle}
            content={formDialogContent}
            dialogActionsProps={formDialogActionsProps}
            open={open}
            onSubmit={handleSubmit}
            onClose={handleCloseFormDialog}
            initialValues={initialValues}
        >
            {({ submitting }) => (
                <Grid item container xs={12}>
                    <Grid item className={classes.gridFields} xs={12} sm={6}>
                        <QCXMoveFocusInside>
                            <QCXSelectTipoDocumentoAutocomplete
                                id="autocomplete-select-tipo-documento-field"
                                key="autocomplete-select-tipo-documento-field"
                                name="tipoDocumento.id"
                                label={t('com.muralis.qcx.documento.tipoDocumento')}
                                fieldProps={{ validate: required }}
                                disabled={submitting || performingAction}
                                required
                                filterParams={tipoDocumentoFilterParams}
                            />
                        </QCXMoveFocusInside>
                    </Grid>
                    <Grid item className={classes.customGridFields} xs={12} sm={6}>
                        <QCXSelectTipoAutocomplete
                            id="text-field-tipo-documento"
                            key="text-field-tipo-documento"
                            name="tipo"
                            label={t('com.muralis.qcx.tipo')}
                            fieldProps={{ validate: required }}
                            disabled={submitting || performingAction}
                            required
                        />
                    </Grid>
                    <Grid item className={classes.gridFields} xs={12}>
                        <QCXFinalTextField
                            id="text-field-descricao-documento"
                            key="text-field-descricao-documento"
                            name="nome"
                            label={t('com.muralis.qcx.descricao')}
                            maxLength={100}
                            disabled={submitting || performingAction}
                            required
                        />
                    </Grid>
                    <Grid item className={classes.gridFields} xs={12}>
                        <QCXFinalCustomInputFile
                            id="upload-input-documento-field"
                            key="upload-input-documento-field"
                            name="documento"
                            label={t('com.muralis.qcx.origem')}
                            tooltipTitle={t('com.muralis.qcx.documento.uploadDocumento')}
                            accept={MediaType.ALL}
                            fieldProps={{ validate: required }}
                            disableRemoveControl
                            disabled={submitting || performingAction}
                            required={!isEditMode}
                        />
                    </Grid>
                    <Grid item className={classes.gridFields} xs={12} sm={6}>
                        <QCXFinalTextField
                            id="text-field-numero-documento"
                            key="text-field-numero-documento"
                            name="numero"
                            label={t('com.muralis.qcx.numero')}
                            maxLength={20}
                            disabled={submitting || performingAction}
                        />
                    </Grid>
                    <Grid item className={classes.customGridFields} xs={12} sm={6}>
                        <QCXFinalDatePickerField
                            id="date-picker-vencimento-documento"
                            key="date-picker-vencimento-documento"
                            name="dataVencimento"
                            label={t('com.muralis.qcx.vencimento')}
                            disabled={submitting || performingAction}
                        />
                    </Grid>
                    <Grid item className={classes.customGridFields} xs={12} sm={4} style={{ paddingTop: '24px' }}>
                        <QCXFinalCheckboxField
                            id="notifica-vencimento-email"
                            key="notifica-vencimento-email"
                            name="notificar"
                            label="Notificar Vencimento por E-mail"
                        />
                    </Grid>
                </Grid>
            )}
        </QCXFinalFormDialog>
    )
}